import { FC, memo, MouseEvent, startTransition, useRef } from 'react';
import { ERROR_ADD_PENNY_PRODUCT_MESSAGE, SPECIAL_METHOD } from '@/constants';

import classNames from 'classnames';
import { cloneDeep } from 'lodash';

import { useAnalytics, useEcomStoreSelector, useUiStoreSelector } from '@/data';
import { CartLineItem } from '@/types';
import { useBundleSpecial, useNotify, useProducts } from '@/hooks';

import { IconSvg } from '../IconSvg';
import { Button } from '../Button';

import style from './counter.module.scss';
import { buildBogoClassifyParams } from '../../hooks/useBundleSpecial/bogoClassify';
import {
  notifyNonBogoBundleAdd,
  runBogoIncrementNotify,
} from './counterDealNotify';

import { canAddSpecialProductToCart } from './Utils';
import { useSpecialsContext } from '@/context';
import { isBogoGetProduct } from '@/utils/bogoSpecialCart';

interface CounterActionsProps {
  product: CartLineItem;
  onKiosk?: boolean;
  index: number;
  quantity: number;
  weightVariant?: string;
  className?: string;
}

const CounterActions: FC<CounterActionsProps> = ({
  product,
  onKiosk,
  index,
  quantity,
  weightVariant,
  className,
}) => {
  const {
    addAndRemoveProductAttributes,
    measureAddToCartGA4,
    measureRemoveFromCartGA4,
    measureSurfsideAction,
  } = useAnalytics();
  const { setRemoveBUYProductBundleId } = useSpecialsContext();
  const { notify } = useNotify();
  const { line_items } = useEcomStoreSelector(['line_items']);
  const { setState: setUiState } = useUiStoreSelector([]);
  const { products, update } = useProducts();
  const changeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const currentProduct = line_items?.[index];

  const bundleId = currentProduct?.bundle_id;

  const {
    currentBundleProducts,
    dependentProducts,
    independentProducts,
    special,
  } = useBundleSpecial({
    id: bundleId ?? '',
  });

  const bundleClassifyParams =
    special?.method === SPECIAL_METHOD.BOGO
      ? buildBogoClassifyParams(special, currentBundleProducts, products)
      : undefined;

  const updateQuantity = (qty: number) => {
    measureAddToCartGA4(product, qty);

    const newProducts = cloneDeep(products);
    newProducts[index].quantity = qty;

    // Disable it for now
    // if (calculateTotalGrams(newProducts) > gramsLimit) {
    //   notify('error', GRAM_LIMIT_EXCEEDED_ERROR);
    //   return;
    // }

    update(newProducts);
  };

  const change = (
    event: MouseEvent<Element, globalThis.MouseEvent>,
    operation: 'minus' | 'plus',
  ) => {
    event?.stopPropagation();
    event?.preventDefault();
    const _quantity = operation === 'minus' ? quantity - 1 : quantity + 1;
    const salePrice = currentProduct?.weightVariant?.salePrice ?? Infinity;
    const incrementLineId = currentProduct?.id ?? undefined;

    /**
     * Check if the product is of type GET (penny) and if the bundle has started, and in case it's complete,
     * we don't allow adding the GET product
     */
    if (
      operation === 'plus' &&
      !canAddSpecialProductToCart({
        special,
        price: salePrice,
        independentProductsInCart: independentProducts,
      })
    ) {
      notify('error', ERROR_ADD_PENNY_PRODUCT_MESSAGE);
      return;
    }

    if (operation === 'plus') {
      measureSurfsideAction([{ ...product, quantity: 1 }], 'add');
    }

    if (operation === 'minus') {
      measureSurfsideAction([{ ...product, quantity: 1 }], 'remove');
    }

    /*     Here we validate whether the user wants to delete a BUY product while there are GET products in the cart. 
    In that case, we assign its bundleId so that the modal appears and allows the user to delete all products belonging 
    to that bundle */
    const isBogo = special?.method === SPECIAL_METHOD.BOGO;
    const isBuyLine =
      isBogo && special
        ? !isBogoGetProduct(special, currentProduct?.id ?? '', salePrice)
        : false;

    const shouldConfirmRemoveBuyWithGetInCart =
      operation === 'minus' &&
      !!dependentProducts?.length &&
      (isBogo ? isBuyLine : !!product?.independent) &&
      (!isBogo
        ? independentProducts?.length <=
          (special?.independentProductsRequired ?? 0)
        : true);

    if (shouldConfirmRemoveBuyWithGetInCart) {
      setRemoveBUYProductBundleId(bundleId);
      setUiState({ is_special_bogo_modal_open: true });
      return;
    }

    if (_quantity === 0) {
      measureRemoveFromCartGA4(product as CartLineItem);

      const newLineItems = line_items?.filter(p => {
        const _id = product?._id;
        return _id
          ? p?._id !== _id
          : weightVariant
          ? p?.id !== product?.id && p?.weightVariant?.id !== weightVariant
          : p?.id !== product?.id;
      });

      // update cart line items
      update(newLineItems);

      // close cart if no items in cart
      if (newLineItems?.length === 0) {
        setUiState({ is_cart_open: false });
      }

      return;
    }

    clearTimeout(changeTimeoutRef.current as any);

    // this is to notify user when they add a product
    // to a bundle
    let bogoOpenCart = false;

    if (bundleId && operation === 'plus' && special) {
      if (isBogo && bundleClassifyParams && incrementLineId) {
        const isGetIncrement = isBogoGetProduct(
          special,
          currentProduct?.id ?? '',
          salePrice,
        );

        bogoOpenCart = runBogoIncrementNotify({
          notify,
          special,
          classifyParams: bundleClassifyParams,
          incrementLineId,
          isGetIncrement,
          productName: product?.name,
        });
      } else if (!isBogo) {
        notifyNonBogoBundleAdd({
          notify,
          special,
          productName: product?.name,
          dependentProductsCount: dependentProducts.length,
          independentProductsCount: independentProducts.length,
        });
      }
    }

    if (onKiosk) {
      changeTimeoutRef.current = setTimeout(() => {
        startTransition(() => {
          updateQuantity(_quantity);
          bogoOpenCart && setUiState({ is_cart_open: true });
        });
      }, 300);

      return;
    }

    startTransition(() => {
      updateQuantity(_quantity);
      bogoOpenCart && setUiState({ is_cart_open: true });
    });
  };

  const quantityLabel = `${quantity} ${quantity === 1 ? 'item' : 'items'}`;
  const counterAriaLabel = `Quantity selector, current quantity: ${quantityLabel}`;

  return (
    <div
      aria-label={counterAriaLabel}
      className={classNames(style.counter__value, className)}
      onClick={e => {
        e.stopPropagation();
        e.preventDefault();
      }}
      role="group"
    >
      <Button
        ariaLabel={`Decrement quantity. Current quantity: ${quantity}`}
        className={style.counter__operation}
        color="link"
        icon
        onClick={e => change(e, 'minus')}
        {...(addAndRemoveProductAttributes(
          'removeFromCart',
          product as CartLineItem,
        ) as any)}
      >
        <IconSvg name={quantity === 1 ? 'delete' : 'minus'} />
      </Button>
      <div
        aria-label={`Quantity: ${quantityLabel}`}
        aria-live="polite"
        className={style.counter__count}
        role="status"
      >
        {quantity}
      </div>
      <Button
        ariaLabel={`Increment quantity. Current quantity: ${quantity}`}
        className={classNames(
          style.counter__operation,
          style.counter__operation_rigth,
        )}
        color="link"
        icon
        onClick={e => change(e, 'plus')}
        {...(addAndRemoveProductAttributes(
          'addToCart',
          product as CartLineItem,
        ) as any)}
      >
        <IconSvg name="plus" />
      </Button>
    </div>
  );
};

const memoizedCounterActions = memo(CounterActions);

export { memoizedCounterActions as CounterActions };
